import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { WarrantyField } from "./WarrantyField";
import { ImagePicker } from "./ImagePicker";
import { AccessoryFields } from "./AccessoryFields";
import { PartsFields } from "./PartsFields";
import { InstallmentFields } from "./InstallmentFields";
import { StockModelFields } from "./StockModelFields";
import { chip } from "@/components/site/OrderSpecFields";

import { CATEGORIES, MODELS } from "@/lib/store";

const EMPTY = { model: "", category: "iphone", storage: "", color: "", condition: "seminovo", battery: "", price: "", old_price: "", installment: "", installments: [], images: [], notes: "", status: "disponivel", warranty: "", acc_type: "", compatible: [], wattage: "", accidental: false, featured: false, featured_until: null, parts_status: "", parts: [], connector: "", base_model: "", apple_warranty_until: "", warranty_kind: "loja", specs: {} };
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const inDays = (n) => new Date(Date.now() + n * 86400000).toISOString();
const CONDITIONS = [["novo", "Novo • Lacrado (Americano)"], ["anatel", "Novo • Lacrado (Anatel)"], ["seminovo", "Seminovo"]];
const TITLES = { new: "Novo produto", edit: "Editar produto", copy: "Duplicar produto" };
const ACC_CONDITIONS = [["novo", "Novo"], ["seminovo", "Seminovo"]];
const WARRANTIES = [["", "Automático pelo tipo"], ["3anos", "3 anos (fonte, cabo)"], ["3meses", "3 meses (película, capa…)"]];
const input = "w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-white/40";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs text-zinc-400">{label}</span>
    {children}
  </label>
);

export const ProductForm = ({ open, onOpenChange, initial, mode = "new", onSave }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const isAcc = form.category === "acessorios";

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial, battery: initial.battery ?? "", price: initial.price || "", old_price: initial.old_price || "", installments: initial.installments || [], specs: initial.specs || {}, base_model: initial.base_model || "", apple_warranty_until: initial.apple_warranty_until || "", warranty_kind: initial.apple_warranty_until ? "apple" : "loja" } : EMPTY);
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.model.trim()) {
      toast.error(isAcc ? "Digite o nome do produto." : "Escolha o modelo.");
      return;
    }
    if (isAcc && !form.acc_type) {
      toast.error("Escolha o tipo de acessório.");
      return;
    }
    if (!isAcc && form.warranty_kind === "apple" && !form.apple_warranty_until) {
      toast.error("Informe até quando vai a garantia Apple.");
      return;
    }
    setBusy(true);
    const used = form.category !== "acessorios" && form.condition === "seminovo";
    const payload = { ...form, battery: form.battery === "" ? null : Number(form.battery), price: Number(form.price) || 0, old_price: Number(form.old_price) || null, parts_status: used ? form.parts_status || "" : "", parts: used && form.parts_status === "trocadas" ? form.parts || [] : [], apple_warranty_until: !isAcc && form.warranty_kind === "apple" ? form.apple_warranty_until : "" };
    delete payload.warranty_kind;
    delete payload.id;
    delete payload.created_at;
    delete payload.sold_at;
    await onSave(payload).finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-form-dialog" className="dark max-h-[92vh] overflow-y-auto border-white/10 bg-[#111113] text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{TITLES[mode]}</DialogTitle>
          <DialogDescription className="text-zinc-400">{mode === "copy" ? "Cópia pronta: mude cor, GB ou bateria e salve para criar o novo aparelho." : "Preencha os dados que vão aparecer na vitrine."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <ImagePicker value={form.images} onChange={set("images")} />
          <div>
            <span className="mb-1.5 block text-xs text-zinc-400">Categoria</span>
            <div data-testid="product-category" className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button type="button" key={c.id} data-testid={`product-category-${c.id}`} aria-pressed={form.category === c.id} className={chip(form.category === c.id)}
                  onClick={() => setForm((f) => (f.category === c.id ? f : { ...f, category: c.id, model: "", base_model: "", specs: {}, storage: "", color: "", battery: c.id === "acessorios" ? "" : f.battery, condition: c.id === "acessorios" && f.condition === "anatel" ? "novo" : f.condition }))}>{c.label}</button>
              ))}
            </div>
          </div>
          {isAcc ? (
            <Field label="Produto">
              <input data-testid="product-model-input" list="product-models" required value={form.model} onChange={set("model")} placeholder={MODELS.acessorios[0]} className={input} />
              <datalist id="product-models">{MODELS.acessorios.map((m) => <option key={m} value={m} />)}</datalist>
            </Field>
          ) : (
            <StockModelFields key={`${form.category}-${initial?.id || "new"}`} form={form} setForm={setForm} />
          )}
          {isAcc && <AccessoryFields form={form} setForm={setForm} />}
          <div>
            <span className="mb-1.5 block text-xs text-zinc-400">Condição</span>
            <div data-testid="product-condition" className="flex flex-wrap gap-2">
              {(isAcc ? ACC_CONDITIONS : CONDITIONS).map(([id, label]) => (
                <button type="button" key={id} data-testid={`product-condition-${id}`} aria-pressed={form.condition === id} onClick={() => set("condition")(id)} className={chip(form.condition === id)}>{label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {isAcc && (
            <Field label="Cor">
              <input data-testid="product-color-input" value={form.color} onChange={set("color")} placeholder="Preto" className={input} />
            </Field>
            )}
            {!isAcc && (
            <Field label="Saúde da bateria (%)">
              <input data-testid="product-battery-input" type="number" min="0" max="100" value={form.battery} onChange={set("battery")} placeholder="Ex: 90" className={input} />
            </Field>
            )}
            {!isAcc && (
            <div className="col-span-2"><WarrantyField form={form} setForm={setForm} input={input} /></div>
            )}
            <Field label="Preço (R$)">
              <input data-testid="product-price-input" type="number" min="0" step="0.01" value={form.price} onChange={set("price")} placeholder="0 = sob consulta" className={input} />
            </Field>
            <Field label="Preço anterior (opcional)">
              <input data-testid="product-old-price-input" type="number" min="0" step="0.01" value={form.old_price} onChange={set("old_price")} placeholder="Preenchido sozinho ao baixar o preço" className={input} />
            </Field>
            <Field label="Status">
              <select data-testid="product-status-select" value={form.status} onChange={set("status")} className={input}>
                <option value="disponivel" className="bg-zinc-900">Disponível</option>
                <option value="vendido" className="bg-zinc-900">Vendido</option>
              </select>
            </Field>
          </div>
          {!isAcc && form.condition === "seminovo" && <PartsFields form={form} setForm={setForm} />}
          {isAcc && (
            <div>
              <span className="mb-1.5 block text-xs text-zinc-400">Garantia HPrime</span>
              <div data-testid="product-warranty" className="flex flex-wrap gap-2">
                {WARRANTIES.map(([id, label]) => (
                  <button type="button" key={id || "auto"} data-testid={`product-warranty-${id || "auto"}`} aria-pressed={form.warranty === id} onClick={() => set("warranty")(id)} className={chip(form.warranty === id)}>{label}</button>
                ))}
              </div>
            </div>
          )}
          {isAcc && (
            <label className="flex items-start gap-3 rounded-xl border border-amber-300/30 bg-amber-300/10 px-3.5 py-3 text-sm text-white">
              <input data-testid="product-accidental-checkbox" type="checkbox" checked={!!form.accidental} onChange={(e) => setForm((f) => ({ ...f, accidental: e.target.checked }))} className="mt-0.5 h-4 w-4 accent-amber-300" />
              <span>Quebra acidental · 6 meses<span className="block text-xs text-zinc-400">Protege caso o aparelho quebre usando este acessório</span></span>
            </label>
          )}
          <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3.5 py-3 text-sm text-white">
            <input data-testid="product-featured-checkbox" type="checkbox" checked={!!form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked, featured_until: e.target.checked ? f.featured_until || inDays(7) : null }))} className="h-4 w-4 accent-amber-300" />
            Oferta da semana (aparece em destaque no topo do site)
          </label>
          {form.featured && (
            <Field label="A oferta termina em (contagem regressiva)">
              <input data-testid="product-featured-until-input" type="datetime-local" required value={toLocalInput(form.featured_until)} onChange={(e) => setForm((f) => ({ ...f, featured_until: e.target.value ? new Date(e.target.value).toISOString() : null }))} className={`${input} [color-scheme:dark]`} />
            </Field>
          )}
          <InstallmentFields price={form.price} selected={form.installments || []} onChange={(installments) => setForm((f) => ({ ...f, installments }))}
            text={form.installment} onTextChange={set("installment")} inputClass={input} />
          <Field label="Observações (opcional)">
            <textarea data-testid="product-notes-input" rows={2} maxLength={400} value={form.notes} onChange={set("notes")} placeholder="Ex: acompanha caixa e cabo" className={input} />
          </Field>
          <button data-testid="admin-save-product-button" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 font-semibold text-black transition-transform active:scale-95 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salvar aparelho
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
